import React, { createContext, useState, useContext, useRef, useCallback } from 'react';
import { detectLanguage, translateText, translationCache, getCacheKey, CACHE_EXPIRY } from '../utils/translator';
 
// Create a context for language management
export const LanguageContext = createContext({
  currentLanguage: 'en',
  setLanguage: () => {},
  translateContent: async () => {},
  detectLanguageContent: async () => {},
});
 
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(
    () => localStorage.getItem('websiteLanguage') || 'en'
  );
  const translations = useRef({}); // Use useRef for caching translations
 
  // Function to set language and update localStorage
  const setLanguage = (lang) => {
    setCurrentLanguage(lang);
    localStorage.setItem('websiteLanguage', lang);
  };
 
  // Function to detect language of the text and set it as the current language
  const detectLanguageContent = async (text) => {
    try {
      const detectedLang = await detectLanguage(text);
      if (detectedLang) {
        setCurrentLanguage(detectedLang);
        localStorage.setItem('websiteLanguage', detectedLang);  // Save detected language in localStorage
      }
    } catch (error) {
      console.error('Language detection error:', error);
    }
  };
 
  // Function to translate content — wrapped in useCallback so its reference is stable
  // and useEffects that depend on it don't fire on every render.
const translateContent = useCallback(async (text, targetLang = currentLanguage) => {
  // If the target language is English, the source text IS English — return as-is
  if (targetLang === 'en') {
    return text;
  }

  // Check if translation already exists in session cache
  const cacheKey = `${text}-${targetLang}`;
  if (translations.current[cacheKey]) {
    return translations.current[cacheKey];
  }

  // Check persistent localStorage-backed cache — instant, no API call needed
  const persistKey = getCacheKey(text, targetLang);
  const persisted = translationCache.get(persistKey);
  if (persisted && Date.now() - persisted.timestamp < CACHE_EXPIRY) {
    translations.current[cacheKey] = persisted.value;
    return persisted.value;
  }

  // Check for specific word translations
  const trimmedText = text.trim();

if (trimmedText === 'ADMINISTRATION SECTION') {
  if (targetLang === 'fr') {
    return ' SECTION ADMINISTRATION';
  }
  return 'ADMINISTRATION SECTION';
}

if (trimmedText === 'Upload') {
  if (targetLang === 'fr') {
    return ' Téléverser';
  }
  return 'Upload';
}

if (trimmedText === 'Admin Approval') {
  if (targetLang === 'fr') {
    return ' Approbation admin';
  }
  return 'Admin Approval';
}


if (trimmedText === 'Delivering Material Intelligence.') {
  if (targetLang === 'fr') {
    return ' Une véritable intelligence des matériaux.';
  }
  return 'Delivering Material Intelligence.';
}

if (trimmedText === 'Founded in 2006, Eagle Vizion was built to redefine material identification through precision engineering and intelligent technology.We began in the demanding PET bottle-to-bottle recycling industry, where purity and performance are critical. Today, we serve MRFs, plastics recyclers, C&D processors, mining and aggregate operations, and the food industry — delivering advanced detection and sorting solutions across diverse material streams.') {
  if (targetLang === 'fr') {
    return 'Fondée en 2006, Eagle Vizion a été créée avec l’ambition de redéfinir l’identification des matériaux grâce à une ingénierie de précision et des technologies intelligentes. Nous avons débuté dans l’industrie exigeante du recyclage bottle-to-bottle du PET, où la pureté et la performance sont essentielles. Aujourd’hui, nous accompagnons les centres de tri (MRF), les recycleurs de plastiques, les opérations de construction et démolition (C&D), le secteur minier et des agrégats, ainsi que l’industrie alimentaire — en offrant des solutions avancées de détection et de tri adaptées à une grande diversité de flux de matériaux.';
  }
  return 'Founded in 2006, Eagle Vizion was built to redefine material identification through precision engineering and intelligent technology.We began in the demanding PET bottle-to-bottle recycling industry, where purity and performance are critical. Today, we serve MRFs, plastics recyclers, C&D processors, mining and aggregate operations, and the food industry — delivering advanced detection and sorting solutions across diverse material streams.';
}

if (trimmedText === 'Our systems combine optical technologies, NIR (Near-Infrared) sensing, AI-driven analytics, and intelligent data platforms to go beyond traditional sorting. We don’t just separate materials — we generate actionable insight that improves yield, reduces losses, and drives smarter operational decisions.') {
  if (targetLang === 'fr') {
    return 'Nos systèmes combinent technologies optiques, capteurs NIR (proche infrarouge), intelligence artificielle et plateformes de données intelligentes afin d’aller au-delà du tri traditionnel. Nous ne faisons pas que séparer les matériaux — nous générons des informations exploitables qui permettent d’améliorer les rendements, de réduire les pertes et de prendre des décisions opérationnelles plus éclairées.';
  }
  return 'Our systems combine optical technologies, NIR (Near-Infrared) sensing, AI-driven analytics, and intelligent data platforms to go beyond traditional sorting. We don’t just separate materials — we generate actionable insight that improves yield, reduces losses, and drives smarter operational decisions.';
}

if (trimmedText === 'Precision Detection. Optimized Sorting. Smarter Operations.Eagle Vizion is not just building machines.\u000bWe are defining the standard in material intelligence.') {
  if (targetLang === 'fr') {
    return 'Détection de précision. Tri optimisé. Opérations plus intelligentes. Eagle Vizion ne construit pas simplement des machines. Nous définissons la norme en intelligence des matériaux.';
  }
  return 'Precision Detection. Optimized Sorting. Smarter Operations.Eagle Vizion is not just building machines.\u000bWe are defining the standard in material intelligence.';
}





if (trimmedText === 'The Eagle Vizion Flake Sorter (FSR) delivers high-precision sorting across a wide range of materials, including PE, PP, PET, PVC, and other polymer streams. Designed for maximum versatility, it excels in applications where traditional gravity chutes fail — particularly with bulkier or irregular materials such as regrind. Its advanced detection and controlled material handling significantly reduce valuable material losses, ensuring that good product stays in the accept stream and not in the reject. The result is higher purity, improved recovery rates, and optimized operational performance.') {
  if (targetLang === 'fr') {
    return 'Le trieur de flocons (FSR) d’Eagle Vizion offre un tri de haute précision pour une large gamme de matériaux, incluant le PE, PP, PET, PVC, ainsi que d’autres flux de polymères. Conçu pour une polyvalence maximale, il excelle dans les applications où les chutes gravitaires traditionnelles échouent — notamment avec des matériaux plus volumineux ou irréguliers comme le broyat (regrind). Sa détection avancée et sa gestion contrôlée du flux de matière réduisent significativement les pertes de matériau valorisable, garantissant que la bonne matière demeure dans le flux d’acceptation et non dans le rejet. Le résultat : une pureté accrue, des taux de récupération supérieurs et une performance opérationnelle optimisée.';
  }
  return 'The Eagle Vizion Flake Sorter (FSR) delivers high-precision sorting across a wide range of materials, including PE, PP, PET, PVC, and other polymer streams. Designed for maximum versatility, it excels in applications where traditional gravity chutes fail — particularly with bulkier or irregular materials such as regrind. Its advanced detection and controlled material handling significantly reduce valuable material losses, ensuring that good product stays in the accept stream and not in the reject. The result is higher purity, improved recovery rates, and optimized operational performance.';
}



if (trimmedText === 'Extensive library generated from the FA series:') {
  if (targetLang === 'fr') {
    return 'Bibliothèque étendue générée à partir de la série FA';
  }
  return 'Extensive library generated from the FA series:';
}

if (trimmedText === 'NIR detection') {
  if (targetLang === 'fr') {
    return 'Détection NIR (proche infrarouge)';
  }
  return 'NIR detection';
}

if (trimmedText === 'AI Detection') {
  if (targetLang === 'fr') {
    return 'Détection par intelligence artificielle (IA)';
  }
  return 'AI Detection';
}

if (trimmedText === 'Color Detection') {
  if (targetLang === 'fr') {
    return 'Détection des couleurs';
  }
  return 'Color Detection';
}

if (trimmedText === 'Particle size 3 – 20mm (can be adapted for smaller fractions)') {
  if (targetLang === 'fr') {
    return 'Granulométrie : 3 à 20 mm (adaptable à des fractions plus fines)';
  }
  return 'Particle size 3 – 20mm (can be adapted for smaller fractions)';
}

if (trimmedText === 'Quality Control Unit (QC) \u2013 With Optional Ejection') {
  if (targetLang === 'fr') {
    return "Unit\u00e9 de Contr\u00f4le Qualit\u00e9 (QC) \u2013 Avec option d'\u00e9jection";
  }
  return 'Quality Control Unit (QC) \u2013 With Optional Ejection';
}

if (trimmedText === 'The Eagle Vizion Quality Control (QC) unit is the next evolution of the FA platform, designed to analyze, monitor a wide variety of streams.') {
  if (targetLang === 'fr') {
    return "L'unit\u00e9 de Contr\u00f4le Qualit\u00e9 (QC) d'Eagle Vizion repr\u00e9sente l'\u00e9volution de la plateforme FA, con\u00e7ue pour analyser et surveiller une grande vari\u00e9t\u00e9 de flux de mati\u00e8res.";
  }
  return 'The Eagle Vizion Quality Control (QC) unit is the next evolution of the FA platform, designed to analyze, monitor a wide variety of streams.';
}

if (trimmedText === 'It can operate as a purely AI-driven inspection system or be combined with NIR hyperspectral imaging for precise material identification and compositional analysis.') {
  if (targetLang === 'fr') {
    return "Elle peut fonctionner comme un syst\u00e8me d'inspection enti\u00e8rement bas\u00e9 sur l'intelligence artificielle ou \u00eatre combin\u00e9e \u00e0 l'imagerie hyperspectrale NIR pour une identification pr\u00e9cise des mat\u00e9riaux et une analyse approfondie de leur composition.";
  }
  return 'It can operate as a purely AI-driven inspection system or be combined with NIR hyperspectral imaging for precise material identification and compositional analysis.';
}

if (trimmedText === 'With optional ejection capability, the QC transitions seamlessly from passive monitoring to active correction \u2014 ensuring stream integrity, improving purity levels, and protecting downstream processes.') {
  if (targetLang === 'fr') {
    return "Gr\u00e2ce \u00e0 son option d'\u00e9jection, la QC passe de mani\u00e8re fluide d'un mode de surveillance passive \u00e0 un mode de correction active \u2014 assurant l'int\u00e9grit\u00e9 des flux, am\u00e9liorant les niveaux de puret\u00e9 et prot\u00e9geant les proc\u00e9d\u00e9s en aval.";
  }
  return 'With optional ejection capability, the QC transitions seamlessly from passive monitoring to active correction \u2014 ensuring stream integrity, improving purity levels, and protecting downstream processes.';
}

if (trimmedText === 'Applications:') {
  if (targetLang === 'fr') {
    return 'Applications\u00a0:';
  }
  return 'Applications:';
}

if (trimmedText === 'PET bottle streams') {
  if (targetLang === 'fr') {
    return 'Flux de bouteilles PET';
  }
  return 'PET bottle streams';
}

if (trimmedText === 'MRF lines') {
  if (targetLang === 'fr') {
    return 'Lignes de centres de tri (MRF)';
  }
  return 'MRF lines';
}

if (trimmedText === 'Construction & Demolition (C&D)') {
  if (targetLang === 'fr') {
    return 'Construction et d\u00e9molition (C&D)';
  }
  return 'Construction & Demolition (C&D)';
}

if (trimmedText === 'Municipal Solid Waste (MSW)') {
  if (targetLang === 'fr') {
    return 'D\u00e9chets municipaux solides (MSW)';
  }
  return 'Municipal Solid Waste (MSW)';
}

if (trimmedText === 'Plastic recycling operations') {
  if (targetLang === 'fr') {
    return 'Installations de recyclage de plastiques';
  }
  return 'Plastic recycling operations';
}

if (trimmedText === 'E-Waste streams') {
  if (targetLang === 'fr') {
    return 'Flux de d\u00e9chets \u00e9lectroniques (E-Waste)';
  }
  return 'E-Waste streams';
}

if (trimmedText === 'The QC is more than a monitoring system \u2014 it is a real-time intelligence platform that transforms data into measurable operational control.') {
  if (targetLang === 'fr') {
    return "La QC est bien plus qu'un syst\u00e8me de surveillance\u00a0: c'est une plateforme d'intelligence en temps r\u00e9el qui transforme les donn\u00e9es en contr\u00f4le op\u00e9rationnel mesurable.";
  }
  return 'The QC is more than a monitoring system \u2014 it is a real-time intelligence platform that transforms data into measurable operational control.';
}

if (trimmedText === 'Welcome to Eagle Vizion') {
  if (targetLang === 'fr') {
    return 'Bienvenue chez Eagle Vizion';
  }
  return 'Welcome to Eagle Vizion';
}

if (trimmedText === 'Founded in 2006, Eagle Vizion engineers advanced material identification and sorting systems that redefine operational performance.') {
  if (targetLang === 'fr') {
    return 'Fond\u00e9e en 2006, Eagle Vizion con\u00e7oit et d\u00e9veloppe des syst\u00e8mes avanc\u00e9s d\u2019identification et de tri des mat\u00e9riaux qui red\u00e9finissent la performance op\u00e9rationnelle.';
  }
  return 'Founded in 2006, Eagle Vizion engineers advanced material identification and sorting systems that redefine operational performance.';
}

if (trimmedText === 'We combine precision optics, NIR and hyperspectral imaging, artificial intelligence, and intelligent data platforms to detect more, sort better, and deliver actionable insight across complex material streams.From PET bottle-to-bottle recycling to MRFs, C&D, advanced polymer processing, mining, and food, our technologies transform material flows into measurable operational advantage.') {
  if (targetLang === 'fr') {
    return 'Nous combinons optiques de pr\u00e9cision, imagerie NIR et hyperspectrale, intelligence artificielle et plateformes de donn\u00e9es intelligentes afin de d\u00e9tecter davantage, trier plus efficacement et fournir des informations exploitables au sein de flux de mati\u00e8res complexes. Du recyclage bottle-to-bottle du PET aux centres de tri (MRF), \u00e0 la construction et d\u00e9molition (C&D), au traitement avanc\u00e9 des polym\u00e8res, au secteur minier et \u00e0 l\u2019industrie alimentaire, nos technologies transforment les flux de mati\u00e8res en avantage op\u00e9rationnel mesurable.';
  }
  return 'We combine precision optics, NIR and hyperspectral imaging, artificial intelligence, and intelligent data platforms to detect more, sort better, and deliver actionable insight across complex material streams.From PET bottle-to-bottle recycling to MRFs, C&D, advanced polymer processing, mining, and food, our technologies transform material flows into measurable operational advantage.';
}

if (trimmedText === 'HOME') {
  if (targetLang === 'fr') {
    return 'ACCUEIL';
  }
  return 'HOME';
}


 
 

  // Perform translation
  try {
    const translatedText = await translateText(text, targetLang);
 
    // Cache the translation
    translations.current[cacheKey] = translatedText;
 
    return translatedText;
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Fallback to original text if translation fails
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [currentLanguage]);



  // Load language from localStorage on initial load — now handled by lazy useState init above
  // useEffect(() => {
  //   const savedLanguage = localStorage.getItem('websiteLanguage');
  //   if (savedLanguage) setCurrentLanguage(savedLanguage);
  // }, []);
 
  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      setLanguage,
      translateContent,
      detectLanguageContent
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
 
// Custom hook for easy context usage
export const useLanguage = () => useContext(LanguageContext);
 
 